import React, { useRef, useState } from "react"
import {
	PieceGlyph,
	SchemaArrow,
	SchemaCourt,
	SchemaPiece,
	arrowScreenPoints,
	pathOf,
	pieceLift,
	piecesByDepth,
} from "../../lesson/schema/SchemaDrawing"
import {
	MAX_SCHEMA_ITEMS,
	ROUND_BEND,
	arrowLength,
	keepOnField,
	newPiece,
	schemaView,
} from "../../utils/exerciseSchema.utils"

// Lo schema di un esercizio, da disegnare sul sito (docs/sito.md). Il campo,
// i pezzi e le frecce sono quelli dell'app (SchemaDrawing): qui intorno c'è
// solo un editor più piccolo di quello vero, coi pezzi che servono per
// provare. Non salva niente: ricaricando la pagina si riparte.

// "Diagonale di rovescio": chi gioca davanti a sinistra scambia in diagonale
// con chi sta in fondo, poi si rimette al centro; il maestro guarda da
// bordo campo, e le coppelle dicono dove deve cadere la palla.
const START = [
	{ type: "player", x: -3.2, y: 12.4, color: "red", ghost: false, num: 0 },
	{ type: "player", x: 3.2, y: -12.4, color: "teal", ghost: false, num: 0 },
	{ type: "coach", x: 6.9, y: 2 },
	{ type: "cone", x: 2.4, y: -9.6, tall: false },
	{ type: "cone", x: 3.9, y: -9.6, tall: false },
	{ type: "arrow", kind: "palla", a: { x: -3.2, y: 11.4 }, b: { x: 3, y: -9.9 }, bend: -ROUND_BEND, both: false },
	{ type: "arrow", kind: "spostamento", a: { x: -2.6, y: 13.3 }, b: { x: -0.4, y: 13.3 }, bend: 0, both: false },
].map((item, i) => ({ ...item, id: `s${i}` }))

const PIECES = [
	{ add: "player", label: "Giocatore", item: { type: "player", color: "red" }, box: "-16 -32 32 36" },
	{ add: "coach", label: "Maestro", item: { type: "coach" }, box: "-16 -26 32 30" },
	{ add: "cone", label: "Coppella", item: { type: "cone" }, box: "-16 -12 32 18" },
]

const TOOLS = [
	{
		tool: "tesa",
		label: "Tesa",
		icon: (
			<>
				<path d="M4 21 L21 8" stroke="currentColor" strokeWidth="2.4" strokeDasharray="4 3" strokeLinecap="round" fill="none" />
				<polygon points="27,4 18,6 23,12" fill="currentColor" />
			</>
		),
	},
	{
		tool: "tonda",
		label: "Tonda",
		icon: (
			<>
				<path d="M3 22 Q4 6 20 6" stroke="currentColor" strokeWidth="2.4" strokeDasharray="4 3" strokeLinecap="round" fill="none" />
				<polygon points="28,6 20,1.5 20,10.5" fill="currentColor" />
			</>
		),
	},
	{
		tool: "spostamento",
		label: "Spostamento",
		icon: (
			<>
				<path d="M4 13 L24 13" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" fill="none" />
				<polygon points="28,13 21,8.5 21,17.5" fill="currentColor" />
			</>
		),
	},
]

const fmt = n => n.toFixed(1)
let nextID = 0
const withID = item => ({ ...item, id: `n${nextID++}` })

export default function DemoSchema() {
	const view = schemaView("persp")
	const svgRef = useRef(null)
	const drag = useRef(null)
	const [items, setItems] = useState(START)
	const [selectedID, setSelectedID] = useState(null)
	const [tool, setTool] = useState(null)
	const isFull = items.length >= MAX_SCHEMA_ITEMS
	const selected = items.find(item => item.id === selectedID)

	const add = type => {
		if (isFull) return
		setTool(null)
		const piece = withID(newPiece(items, type))
		setItems([...items, piece])
		setSelectedID(piece.id)
	}

	const pickTool = name => {
		setSelectedID(null)
		setTool(current => (current === name ? null : name))
	}

	const remove = () => {
		setItems(items.filter(item => item.id !== selectedID))
		setSelectedID(null)
	}

	const restart = () => {
		setItems(START)
		setSelectedID(null)
		setTool(null)
	}

	// Dal dito al campo, in metri, dentro il verde
	const courtPoint = event => {
		const matrix = svgRef.current?.getScreenCTM?.()
		if (!matrix) return null
		const p = new DOMPoint(event.clientX, event.clientY).matrixTransform(matrix.inverse())
		return keepOnField(view.toCourt(p.x, p.y))
	}

	const onPointerDown = event => {
		const at = courtPoint(event)
		if (!at) return
		const target = event.target.closest?.("[data-id]")
		if (tool) {
			if (isFull) return
			event.preventDefault()
			svgRef.current.setPointerCapture?.(event.pointerId)
			const arrow = withID({
				type: "arrow",
				kind: tool === "spostamento" ? "spostamento" : "palla",
				a: at,
				b: at,
				bend: tool === "tonda" ? -ROUND_BEND : 0,
				both: false,
			})
			setItems([...items, arrow])
			setSelectedID(arrow.id)
			drag.current = { id: arrow.id, fresh: true, before: items }
			return
		}
		if (!target) {
			setSelectedID(null)
			drag.current = null
			return
		}
		event.preventDefault()
		svgRef.current.setPointerCapture?.(event.pointerId)
		const item = items.find(other => other.id === target.dataset.id)
		setSelectedID(item.id)
		drag.current = { id: item.id, start: at, original: item }
	}

	const onPointerMove = event => {
		const d = drag.current
		if (!d) return
		const at = courtPoint(event)
		if (!at) return
		setItems(current =>
			current.map(item => {
				if (item.id !== d.id) return item
				if (d.fresh) return { ...item, b: at }
				const o = d.original
				const dx = at.x - d.start.x
				const dy = at.y - d.start.y
				if (o.type === "arrow")
					return {
						...item,
						a: keepOnField({ x: o.a.x + dx, y: o.a.y + dy }),
						b: keepOnField({ x: o.b.x + dx, y: o.b.y + dy }),
					}
				return { ...item, ...keepOnField({ x: o.x + dx, y: o.y + dy }) }
			}),
		)
	}

	const onPointerUp = () => {
		const d = drag.current
		drag.current = null
		if (!d?.fresh) return
		// un tocco senza trascinare non è una freccia: si resta pronti a tirarla
		const arrow = items.find(item => item.id === d.id)
		if (arrow && arrowLength(arrow) < 1) {
			setItems(d.before)
			setSelectedID(null)
		} else setTool(null)
	}

	// Il dito trascina solo quando c'è qualcosa da trascinare: un pezzo
	// scelto, o una freccia da tirare. Il resto del tempo, sul campo la
	// pagina scorre come altrove.
	const holding = Boolean(tool || selectedID)

	return (
		<div className="demo demo-card demo-schema">
			<div className="demo-schema-head">
				<b>Diagonale di rovescio</b>
				<span className="demo-chip is-quiet">Fase tecnica</span>
			</div>
			<div className="demo-schema-field">
				<svg
					ref={svgRef}
					viewBox={`0 0 ${view.width} ${fmt(view.height)}`}
					role="img"
					aria-label="Lo schema dell'esercizio"
					style={{ touchAction: holding ? "none" : "pan-y" }}
					onPointerDown={onPointerDown}
					onPointerMove={onPointerMove}
					onPointerUp={onPointerUp}
					onPointerCancel={onPointerUp}>
					<SchemaCourt view={view} grass={false} />
					{items.map(
						item =>
							item.type === "arrow" && (
								<g key={item.id} data-id={item.id} data-piece="arrow">
									<path
										d={pathOf(arrowScreenPoints(item, view))}
										stroke="transparent"
										strokeWidth="16"
										fill="none"
									/>
									{item.id === selectedID && (
										<path
											d={pathOf(arrowScreenPoints(item, view))}
											className="demo-schema-halo"
											strokeWidth="8"
											fill="none"
										/>
									)}
									<SchemaArrow item={item} view={view} />
								</g>
							),
					)}
					{piecesByDepth(items, view).map(({ item }) => (
						<g key={item.id} data-id={item.id} data-piece={item.type}>
							<SchemaPiece item={item} view={view}>
								{item.id === selectedID && (
									<circle className="demo-schema-ring" cx="0" cy={-pieceLift(item)} r="17" />
								)}
								<circle cx="0" cy={-pieceLift(item)} r="18" fill="transparent" />
							</SchemaPiece>
						</g>
					))}
				</svg>
			</div>

			<div className="demo-schema-tray">
				{PIECES.map(piece => (
					<button key={piece.add} type="button" onClick={() => add(piece.add)} disabled={isFull}>
						<svg viewBox={piece.box} width="30" height="26" aria-hidden="true">
							<PieceGlyph item={piece.item} />
						</svg>
						{piece.label}
					</button>
				))}
				{TOOLS.map(entry => (
					<button
						key={entry.tool}
						type="button"
						aria-pressed={tool === entry.tool}
						className={tool === entry.tool ? "is-on" : undefined}
						onClick={() => pickTool(entry.tool)}>
						<svg viewBox="0 0 30 26" width="30" height="26" aria-hidden="true">
							{entry.icon}
						</svg>
						{entry.label}
					</button>
				))}
			</div>

			<div className="demo-schema-foot">
				{selected ? (
					<button type="button" className="demo-schema-remove" onClick={remove}>
						Togli dal campo
					</button>
				) : (
					<span className="demo-muted">
						{tool ? "Tira la freccia sul campo, col dito." : "Tocca un pezzo, poi trascinalo."}
					</span>
				)}
				<button type="button" className="demo-schema-restart" onClick={restart}>
					Ricomincia
				</button>
			</div>
		</div>
	)
}

